import { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import RpCheckboxGrid from 'components/commons/RpCheckboxGrid';
import RpCheckbox from 'components/commons/RpCheckbox';
import SearchFilterUtil from 'lib/SearchFilterUtil';

function BackButton({ onClick }) {
    return (
        <IconButton
            onClick={onClick} aria-label="back"
            color="primary"
            sx={{
                fontSize: 20,
            }}
        >
            <ArrowBackIcon />
        </IconButton>
    );
}

const StyledButton = styled(Button)({
    letterSpacing: '0.46px',
    fontSize: 14,
});

function RpExpandableCheckboxOptions(props) {
    const [search, setSearch] = useState('');

    const filteredOptions = props.options
        .filter(option => option.label.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => a.label.localeCompare(b.label));

    const columnSize = props.columnSize || 3;
    const totalDisplayed = Math
        .min(filteredOptions.length, props.maxRows ? props.maxRows * columnSize : filteredOptions.length);

    const getTotalInfoMsg = () => {
        if (props.errorMessage) return props.errorMessage;
        const optionsText = props.options.length === 1 ? 'option' : 'options';
        return totalDisplayed
            ? `Displaying ${totalDisplayed} of ${props.options.length} ${optionsText}`
            : 'No matching filter options found';
    };
    const showField = props.options.length > 15;


    return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                justifyContent="space-between"
                mb={1.5}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    {
                        !props.maxRows && <BackButton onClick={props.exitFullView} />
                    }
                    <Typography variant="text1" gutterBottom>
                        <span>{props.title}</span>
                        <span style={{ color: '#0000008a' }}>
                            {props.fullView && props.selected.length ? ` | ${props.selected.length} selected` : ''}
                        </span>
                    </Typography>
                    {
                        showField && props.maxRows &&
                        <Button
                            variant="text"
                            size="small"
                            onClick={props.onViewAll}
                            sx={{
                                fontSize: '13px',
                            }}
                            disabled={!!props.errorMessage}
                        >
                            View All ({SearchFilterUtil.numberWithCommas(props.options.length)})&nbsp;&nbsp;&nbsp;&nbsp;>
                        </Button>
                    }

                </Stack>
                <Stack direction="row" spacing={1}>
                    {
                        props.canSelectAll && (
                            <StyledButton
                                size="small"
                                onClick={props.selectAll}
                                disabled={props.selected.length === props.options.length}
                            >
                                Select All
                            </StyledButton>
                        )
                    }
                    <StyledButton
                        size="small"
                        onClick={props.clearAll}
                        disabled={props.selected.length === 0}
                    >
                        Clear Selected
                    </StyledButton>
                    {showField && (
                        <TextField
                            disabled={!!props.errorMessage}
                            size="small"
                            placeholder={props.placeholder || 'Search options'}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            sx={{
                                mb: 1,
                                ml: '24px !important',
                                fontSize: 13,
                                width: 250,
                                pt: '3px',
                            }}
                            variant="standard"
                        />
                    )}
                </Stack>
            </Stack>
            <Stack spacing={1}>
                <Typography variant="subtitle1" color="textSecondary">
                    {getTotalInfoMsg()}
                </Typography>
            </Stack>
            <Stack
                spacing={1}
                height={{
                    height: 'calc(100% - 95px)',
                }}
            >
                <RpCheckboxGrid
                    columnSize={columnSize}
                    spacing={0}
                    width={props.width}
                    maxRows={props.maxRows}
                >
                    {filteredOptions.map(
                        (option) => (
                        <RpCheckbox
                            value={option.value}
                            label={option.label}
                            checked={props.selected.includes(option.value)}
                            onChange={() => props.onChange(option.value)}
                            key={option.value}
                        />
                    ))}
                </RpCheckboxGrid>
            </Stack>
        </>
    );
}

BackButton.propTypes = {
    onClick: PropTypes.func.isRequired,
};

RpExpandableCheckboxOptions.propTypes = {
    title: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    columnSize: PropTypes.number,
    selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
    onChange: PropTypes.func.isRequired,
    clearAll: PropTypes.func.isRequired,
    selectAll: PropTypes.func,
    onViewAll: PropTypes.func,
    errorMessage: PropTypes.string,
    exitFullView: PropTypes.func,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxRows: PropTypes.number,
    canSelectAll: PropTypes.bool,
};

export default RpExpandableCheckboxOptions;